import React from 'react'
import { useCurrency } from '../context/CurrencyContext'

export default function Summary({ loans, emiPayments, currentMonth }) {
  const { fmt } = useCurrency()
  const total = loans.reduce((s, l) => s + parseFloat(l.balance), 0)
  const monthly = loans.reduce((s, l) => s + parseFloat(l.emi_amount), 0)
  const weightedRate = loans.reduce((s, l) => s + l.interest_rate * l.balance, 0)
  const avgRate = total > 0 ? weightedRate / total : 0

  const thisMonthPayments = emiPayments.filter(p => p.payment_month === currentMonth)
  const paidLoans = thisMonthPayments.filter(p => p.status === 'paid')
  const applicableLoans = loans.filter(l => {
    const p = thisMonthPayments.find(p => p.loan_id === l.id)
    return !p || p.status !== 'na'
  })
  const paidAmt = paidLoans.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0)

  const cards = [
    {
      label: 'Total Debt',
      value: fmt(total),
      sub: `across ${loans.length} loan${loans.length !== 1 ? 's' : ''}`,
      color: '#b8441a'
    },
    {
      label: 'Monthly EMI Due',
      value: fmt(monthly),
      sub: 'total EMI / month',
      color: '#c9a84c'
    },
    {
      label: 'EMI Paid This Month',
      value: fmt(paidAmt),
      sub: `${paidLoans.length} of ${applicableLoans.length} paid`,
      color: '#4a6741'
    },
    {
      label: 'Avg Interest Rate',
      value: avgRate.toFixed(2) + '%',
      sub: 'weighted avg p.a.',
      color: '#3d4a5c'
    }
  ]

  const totalOriginal = loans.reduce((s, l) => s + parseFloat(l.original_amount || l.balance), 0)
  const totalPaid = totalOriginal - total
  const overallPct = totalOriginal > 0 ? Math.min(100, Math.max(0, Math.round((totalPaid / totalOriginal) * 100))) : 0

  return (
    <div style={{ marginBottom: '2rem' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '1rem',
        marginBottom: '1rem'
      }}>
        {cards.map((card, i) => (
          <div key={i} style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            padding: '1.3rem 1.5rem',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'default'
          }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              height: '3px', background: card.color
            }} />
            <div style={{
              fontFamily: 'monospace', fontSize: '0.68rem',
              fontWeight: '500', textTransform: 'uppercase',
              letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.45rem'
            }}>
              {card.label}
            </div>
            <div style={{
              fontFamily: 'Georgia, serif', fontSize: '1.9rem',
              fontWeight: '700', lineHeight: 1, color: 'var(--text)'
            }}>
              {card.value}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.35rem' }}>
              {card.sub}
            </div>
          </div>
        ))}
      </div>

      {loans.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '1.1rem 1.5rem',
          position: 'relative', overflow: 'hidden'
        }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#c9a84c' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.65rem' }}>
            <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}>
              Overall Debt Payoff Progress
            </span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: 'var(--text)' }}>
              {overallPct}%
            </span>
          </div>
          <div style={{ height: '10px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden', marginBottom: '0.65rem' }}>
            <div style={{
              height: '100%', width: overallPct + '%',
              background: overallPct >= 75 ? '#4a6741' : overallPct >= 40 ? '#c9a84c' : '#b8441a',
              borderRadius: '99px', transition: 'width 0.8s ease'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
            <span>Paid off: <span style={{ color: '#4a6741', fontWeight: '600' }}>{fmt(totalPaid)}</span></span>
            <span>Remaining: <span style={{ color: '#b8441a', fontWeight: '600' }}>{fmt(total)}</span></span>
            <span>Original total: <span style={{ color: 'var(--text)', fontWeight: '600' }}>{fmt(totalOriginal)}</span></span>
          </div>
        </div>
      )}
    </div>
  )
}